/**
 * Preload actions for PDF preloading (ENG-2465)
 *
 * These are plain functions that interact with the preloadCache singleton.
 * No Redux - all state lives in preloadCache.js.
 */
import posthog from 'posthog-js';
import { preloadCache, PRELOAD_CONFIG } from '../utils/preloadCache';
import { shouldEnablePreloading } from '../utils/mobileDetect';

/**
 * Capture PostHog analytics event (skipped in development and test).
 * Analytics is a side effect that must never break the user action that
 * triggered it, so the capture is wrapped -- a posthog SDK throw is swallowed.
 */
export const captureAnalytics = (eventName, properties) => {
  if (
    process.env.NODE_ENV !== 'development'
    && process.env.NODE_ENV !== 'test'
    && posthog
    && typeof posthog.capture === 'function'
  ) {
    try {
      posthog.capture(eventName, properties);
    } catch (error) {
      console.warn('[analytics] captureAnalytics failed:', error);
    }
  }
};

const retryTimeouts = new Map();

const clearRetryTimeout = (fileToken) => {
  const timeoutId = retryTimeouts.get(fileToken);
  if (timeoutId) {
    clearTimeout(timeoutId);
    retryTimeouts.delete(fileToken);
  }
};

const clearAllRetryTimeouts = () => {
  retryTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
  retryTimeouts.clear();
};

const scheduleRetry = (fileToken, isUserClick) => {
  clearRetryTimeout(fileToken);

  const timeoutId = setTimeout(() => {
    retryTimeouts.delete(fileToken);

    const latestEntry = preloadCache.get(fileToken);
    if (!latestEntry || latestEntry.blobStatus !== 'pending' || !latestEntry.signedUrl) {
      return;
    }

    doPreloadRecord(fileToken, latestEntry.signedUrl, isUserClick); // eslint-disable-line no-use-before-define
  }, PRELOAD_CONFIG.RETRY_DELAY_MS);

  retryTimeouts.set(fileToken, timeoutId);
};

/**
 * Evict a specific entry from the preload cache (e.g., before annotation save replaces the record)
 * @param {string} fileToken
 */
export const evictCacheEntry = (fileToken) => {
  preloadCache.evictEntry(fileToken);
};

/**
 * Get cached blob URL for a record (pure getter, no side effects)
 * @param {string} fileToken
 * @returns {string|null}
 */
export const getCachedBlobUrl = (fileToken) => {
  const entry = preloadCache.get(fileToken);
  if (entry?.blobStatus === 'ready' && entry?.blobUrl) {
    return entry.blobUrl;
  }
  return null;
};

/**
 * Clear the preload cache (e.g., on tab switch)
 * @param {string} reason - Reason for clearing: 'tab_switch', 'data_refresh', etc.
 */
export const clearPreloadCache = (reason) => {
  // Preserve currently viewed record during data refresh to avoid invalidating active viewer's blob URL
  const preserveCurrentViewer = reason === 'data_refresh';

  clearAllRetryTimeouts();
  preloadCache.clear({ preserveCurrentViewer });
};

/**
 * Set the currently viewed record (to skip during TTL refresh)
 * @param {string|null} fileToken
 */
export const setCurrentlyViewedRecord = (fileToken) => {
  preloadCache.setCurrentlyViewedRecord(fileToken);
};

/**
 * Internal: Start preloading blob candidates
 * Note: Uses function declaration for hoisting (mutually recursive with doPreloadRecord)
 */
function startPreloadingCandidates() {
  // Skip on mobile
  if (!shouldEnablePreloading()) {
    return;
  }

  const candidates = preloadCache.getPreloadCandidates();

  // Preload candidates - stop when we can't start more
  candidates.some((cacheEntry) => {
    if (!preloadCache.canStartPreload()) {
      // Max concurrent reached, stop iteration
      return true;
    }
    doPreloadRecord(cacheEntry.fileToken, cacheEntry.signedUrl, false); // eslint-disable-line no-use-before-define
    return false;
  });
}

/**
 * Internal function to preload a single record
 * @param {string} fileToken
 * @param {string} signedUrl
 * @param {boolean} isUserClick
 */
async function doPreloadRecord(fileToken, signedUrl, isUserClick) {
  if (!signedUrl) return;

  clearRetryTimeout(fileToken);

  // Check if we can start (user clicks bypass limit)
  if (!preloadCache.canStartPreload(isUserClick)) {
    return;
  }

  const entry = preloadCache.get(fileToken);
  if (!entry) return;

  // Skip if already ready or in progress
  if (entry.blobStatus === 'ready' || entry.blobStatus === 'fetching') {
    return;
  }

  // Skip large files
  if (entry.isLargeFile || entry.numPages > PRELOAD_CONFIG.MAX_PAGES_FOR_BLOB) {
    return;
  }

  const controller = preloadCache.startPreload(fileToken);
  if (!controller) return;

  try {
    const response = await fetch(signedUrl, {
      credentials: 'omit',
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    // Get content length to check size
    const contentLength = parseInt(response.headers.get('content-length') || '0', 10);

    // Check if file is too large
    if (contentLength > PRELOAD_CONFIG.MAX_SIZE_MB_FOR_BLOB * 1024 * 1024) {
      preloadCache.completePreload(fileToken, null, contentLength); // Will be marked as skipped
      // Continue preloading next candidates
      startPreloadingCandidates();
      return;
    }

    const blob = await response.blob();
    const sizeBytes = blob.size;

    preloadCache.completePreload(fileToken, blob, sizeBytes);
    clearRetryTimeout(fileToken);

    // Continue preloading next candidates
    startPreloadingCandidates();
  } catch (error) {
    if (error.name === 'AbortError') {
      // Request was cancelled - nothing to do
      return;
    }

    const shouldRetry = entry.retryCount < 1;
    preloadCache.failPreload(fileToken, shouldRetry);

    // If we should retry, schedule it
    if (shouldRetry) {
      scheduleRetry(fileToken, isUserClick);
    } else {
      clearRetryTimeout(fileToken);
      // Continue with next candidates
      startPreloadingCandidates();
    }
  }
}

/**
 * Initialize preload cache from list API records
 * @param {Array} records - Records from list API with signed_url
 */
export const initializePreloadCache = (records) => {
  // Skip on mobile devices
  if (!shouldEnablePreloading()) {
    return;
  }

  // Initialize cache entries from record data
  records.forEach((record) => {
    if (record.signed_url) {
      preloadCache.initializeFromListData(record);
    }
  });

  // Start preloading candidates
  startPreloadingCandidates();
};
